import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { FeedbackCandidate } from '../services/feedback-tracking.service';
import { FeedbackType } from '../Models/FeedbackType';
import { FeedbackItem } from '../Models/FeedbackItem';

interface FeedbackReviewRow extends FeedbackCandidate {
  include: boolean;
  feedbackTypeId: number | null;
  comments: string;
}

@Component({
  selector: 'app-feedback-review-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule],
  templateUrl: './feedback-review-dialog.component.html',
  styleUrls: ['./feedback-review-dialog.component.css']
})
export class FeedbackReviewDialogComponent implements OnInit {
  rows: FeedbackReviewRow[];
  feedbackTypes: FeedbackType[] = [];
  loadingFeedbackTypes = true;
  feedbackTypesLoadFailed = false;

  constructor(
    private dialogRef: MatDialogRef<FeedbackReviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: { candidates: FeedbackCandidate[] },
    private http: HttpClient,
    private toastr: ToastrService
  ) {
    this.rows = (JSON.parse(JSON.stringify(data.candidates)) as FeedbackCandidate[]).map(c => ({
      ...c,
      include: true,
      feedbackTypeId: c.suggestedFeedbackTypeId ?? null,
      comments: ''
    }));
  }

  ngOnInit(): void {
    this.http.get<FeedbackType[]>('/charitycentral/getfeedbacktypes').subscribe({
      next: (types) => {
        this.feedbackTypes = types || [];
        this.loadingFeedbackTypes = false;
        if (this.feedbackTypes.length === 0) {
          // Request succeeded but returned no rows - not an HTTP error, but just as unusable.
          // Most likely cause: the FeedbackType table is empty (or this server is pointed at a
          // different database than the one it was seeded in).
          this.feedbackTypesLoadFailed = true;
          this.toastr.error('No feedback types were returned from the server - the dropdowns below will be empty. Check the FeedbackType table has data in the database this server is connected to.', '', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
        }
      },
      error: (err) => {
        console.error('Error loading feedback types:', err);
        this.loadingFeedbackTypes = false;
        this.feedbackTypesLoadFailed = true;
        this.toastr.error('Failed to load feedback types - the dropdowns below will be empty. Check the FeedbackType table exists in the database this server is connected to.', '', { timeOut: 0, extendedTimeOut: 0, closeButton: true });
      }
    });
  }

  canApprove(): boolean {
    const included = this.rows.filter(r => r.include);
    return included.length > 0 && included.every(r => !!r.feedbackTypeId);
  }

  approve(): void {
    if (!this.canApprove()) return;

    const items: FeedbackItem[] = this.rows
      .filter(r => r.include)
      .map(r => ({
        feedbackTypeId: r.feedbackTypeId!,
        beforeValue: r.beforeValue,
        afterValue: r.afterValue,
        comments: r.comments?.trim() ? r.comments.trim() : null
      }));

    this.dialogRef.close(items);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
